import { observer } from 'mobx-react';
import { useState, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import { defaultSchema } from 'rehype-sanitize';
import IconButton, { ICON_BUTTON_ICONS } from '../../../buttons/IconButton';
import AgentDataTable from '../../table/AgentDataTable';
import AgentMessageLoading from './AgentMessageLoading';
import AgentEnrichmentCards from '../../enrichment/AgentEnrichmentCards';
import HorizontalScrollContainer from '../../common/HorizontalScrollContainer';
import messagePhaseManager, { MessagePhase } from '../../utils/MessagePhaseManager';
import PropTypes from 'prop-types';

// TD-1728: Security configuration for HTML in markdown
// Allows safe HTML tags (br, strong, em, etc.) while blocking dangerous ones (script, iframe, etc.)
const sanitizeSchema = {
  ...defaultSchema,
  tagNames: [
    ...(defaultSchema.tagNames || []),
    'br' // TD-1728: Allow <br> tags for line breaks in table cells
  ],
  attributes: {
    ...defaultSchema.attributes,
    // Ensure no dangerous attributes are allowed
    '*': ['className', 'id'] // Allow only safe global attributes
  }
};

const LinkRenderer = ({ href, children }) => {
  const handleClick = (e) => {
    e.preventDefault();
    window.open(href, '_blank');
  };
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" onClick={handleClick}>
      {children}
    </a>
  );
};

LinkRenderer.propTypes = {
  href: PropTypes.string,
  children: PropTypes.node
};

// TD-1729: Custom table renderer for markdown tables
// Wraps tables in HorizontalScrollContainer for horizontal scrolling with fade edges
const TableRenderer = ({ children }) => {
  return (
    <HorizontalScrollContainer className="markdown-table-wrapper">
      <table>{children}</table>
    </HorizontalScrollContainer>
  );
};

TableRenderer.propTypes = {
  children: PropTypes.node
};

const AgentAIMessage = observer(
  ({
    content,
    contentType = 'text',
    tableData = null,
    agentName = 'Agentic AI',
    // NEW: Phase-based message support
    message = null,
    onRetry = null,
    // TD-2293: sends a hidden enrichment control reply (reveal confirm/cancel)
    onControlReply = null,
    // TD-2293: called when a reveal job settles live, to reconcile the credit chip
    onRevealComplete = null,
    // TD-2884: total contact-credit allowance, the denominator on the confirm card
    creditLimit = null,
    // ✨ CLAUDE DESKTOP UX: Enable clean thinking phase display
    showClaudeDesktopStyle = false
  }) => {
    // BACKWARD COMPATIBILITY: Create message object if not provided
    const messageObj = message || {
      content,
      contentType,
      tableData,
      phase: MessagePhase.RESPONSE, // Default to response phase for backward compatibility
      isComplete: true,
      toolResults: tableData ? { type: 'table', data: tableData } : null
    };

    // ENHANCED BACKWARD COMPATIBILITY: Handle legacy messages without phase field
    if (messageObj && !messageObj.phase) {
      messageObj.phase = MessagePhase.RESPONSE; // Default legacy messages to response phase
      messageObj.isComplete = true; // Legacy messages are always complete
    }

    // TD-1741: Ref to capture rendered HTML for clipboard
    const contentRef = useRef(null);

    // COPY STATE: Dynamic tooltip based on message content
    const hasTableData = messageObj.toolResults?.type === 'table' || contentType === 'table';
    const [copyState, setCopyState] = useState({
      status: 'idle', // 'idle', 'copying', 'success', 'error'
      tooltip: hasTableData ? 'Copy as markdown table' : 'Copy to clipboard'
    });

    const handleCopyToClipboard = async () => {
      try {
        setCopyState({ status: 'copying', tooltip: 'Copying...' });

        // TD-1741: Get rendered HTML directly from DOM
        const htmlContent = contentRef.current ? contentRef.current.innerHTML : '';

        // PHASE-AWARE COPY: Get content and table data for markdown format
        const messageContent = messageObj.content || content;
        const messageTableData = messageObj.toolResults?.data || tableData;
        const messageContentType = messageObj.toolResults?.type || contentType;

        let markdownText = '';

        if (!messageTableData || messageContentType !== 'table') {
          // Text-only content
          markdownText = messageContent;
        } else {
          // Content with table data - generate markdown table
          const formatCellValue = (value) => {
            if (value === null || value === undefined) return '';
            const stringValue = value.toString();
            return stringValue.replace(/\|/g, '\\|').replace(/\n/g, '<br>');
          };

          const headers = messageTableData.columns.map((col) => col.label || col.key);
          const headerRow = `| ${headers.join(' | ')} |`;
          const separatorRow = `| ${headers.map(() => '---').join(' | ')} |`;

          const dataRows = messageTableData.rows.map(
            (row) =>
              `| ${messageTableData.columns
                .map((col) => formatCellValue(row[col.key]))
                .join(' | ')} |`
          );

          const markdownTable = [headerRow, separatorRow, ...dataRows].join('\n');
          markdownText = `${messageContent}\n\n${markdownTable}`;
        }

        // TD-1741: Multi-format clipboard (HTML for Word, markdown for plain text)
        if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
          const htmlBlob = new Blob([htmlContent], { type: 'text/html' });
          const textBlob = new Blob([markdownText], { type: 'text/plain' });

          const clipboardItem = new ClipboardItem({
            'text/html': htmlBlob,
            'text/plain': textBlob
          });

          await navigator.clipboard.write([clipboardItem]);
        } else if (navigator.clipboard && window.isSecureContext) {
          // Fallback: writeText with markdown only
          await navigator.clipboard.writeText(markdownText);
        } else {
          // Fallback for older browsers
          await fallbackCopyToClipboard(markdownText);
        }

        setCopyState({ status: 'success', tooltip: 'Copied!' });

        // Reset tooltip after 2 seconds
        setTimeout(() => {
          const defaultTooltip = hasTableData ? 'Copy as markdown table' : 'Copy to clipboard';
          setCopyState({ status: 'idle', tooltip: defaultTooltip });
        }, 2000);
      } catch (error) {
        console.error('Failed to copy to clipboard:', error);
        setCopyState({ status: 'error', tooltip: 'Copy failed' });

        // Reset tooltip after 3 seconds
        setTimeout(() => {
          const defaultTooltip = hasTableData ? 'Copy as markdown table' : 'Copy to clipboard';
          setCopyState({ status: 'idle', tooltip: defaultTooltip });
        }, 3000);
      }
    };

    // Fallback copy method for older browsers
    const fallbackCopyToClipboard = (text) => {
      return new Promise((resolve, reject) => {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        textArea.style.top = '-9999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
          const successful = document.execCommand('copy');
          document.body.removeChild(textArea);
          if (successful) {
            resolve();
          } else {
            reject(new Error('Fallback copy failed'));
          }
        } catch (err) {
          document.body.removeChild(textArea);
          reject(err);
        }
      });
    };

    const handleTableAction = (actionType, data) => {
      console.log('Table action:', actionType, data);
      // TODO: Implement table actions for embedded tables
    };

    const handleRetry = () => {
      console.log('Retrying message:', messageObj.id, messageObj.content || content);
      if (onRetry && messageObj.id) {
        onRetry(messageObj.id);
      }
    };

    const renderPhaseContent = () => {
      const displayContent = messagePhaseManager.getDisplayContent(messageObj);

      switch (displayContent.type) {
        case 'status':
          return (
            <div className={`agent-phase-status phase-${messageObj.phase}`}>
              <div className="phase-indicator">
                {displayContent.icon && <span className="phase-icon">{displayContent.icon}</span>}
                <span className="phase-text">{displayContent.text}</span>
              </div>
            </div>
          );

        case 'tool_results':
          return (
            <div className="agent-phase-tool-results">
              <div className="tool-results-header">
                {displayContent.icon && <span className="phase-icon">{displayContent.icon}</span>}
                <span>
                  Found {displayContent.toolResults?.data?.metadata?.totalResults || 0} results
                </span>
              </div>
              {displayContent.toolResults && displayContent.toolResults.type === 'table' && (
                <div className="embedded-tool-table">
                  <AgentDataTable
                    columns={displayContent.toolResults.data.columns}
                    rows={displayContent.toolResults.data.rows}
                    metadata={displayContent.toolResults.data.metadata}
                    onAction={handleTableAction}
                    embedded={true}
                    compact={true}
                    maxHeight="400px"
                  />
                </div>
              )}
            </div>
          );

        case 'response':
          return (
            <div className="agent-phase-response">
              {/* Preserve tool results if they exist */}
              {displayContent.showResults && displayContent.toolResults && (
                <div className="preserved-tool-results">
                  <div className="preserved-results-header">📊 Search Results</div>
                  <AgentDataTable
                    columns={displayContent.toolResults.data.columns}
                    rows={displayContent.toolResults.data.rows}
                    metadata={displayContent.toolResults.data.metadata}
                    onAction={handleTableAction}
                    embedded={true}
                    readonly={true}
                    showFilters={false}
                  />
                </div>
              )}

              {/* Final AI response text */}
              {displayContent.text && (
                <div className="ai-response-text">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[rehypeRaw, [rehypeSanitize, sanitizeSchema]]}
                    components={{ a: LinkRenderer, table: TableRenderer }}
                  >
                    {displayContent.text}
                  </ReactMarkdown>
                </div>
              )}

              {/* TD-2293: enrichment cards (reveal confirm, later discovery/
                  progress) parsed from the agent's tags into message.enrichment */}
              <AgentEnrichmentCards
                message={messageObj}
                onControlReply={onControlReply}
                onRevealComplete={onRevealComplete}
                creditLimit={creditLimit}
              />

              {/* TD-2347: inline note when the user stopped this turn */}
              {displayContent.stopped && (
                <div className="agent-stopped-note">Stopped by user</div>
              )}
            </div>
          );

        case 'error':
          return (
            <div className="agent-ai-message-error-content">
              <div className="error-icon">{displayContent.icon}</div>
              <div className="error-message">
                <div className="error-main-text">{displayContent.text}</div>
                {messageObj.error?.name && messageObj.error?.message && (
                  <details className="error-details">
                    <summary>Technical Details</summary>
                    <div className="error-technical">
                      <strong>{messageObj.error.name}:</strong> {messageObj.error.message}
                      <br />
                      <small>
                        Time: {new Date(messageObj.error.timestamp).toLocaleTimeString()}
                      </small>
                    </div>
                  </details>
                )}
              </div>
              {displayContent.showRetry && (
                <div className="error-retry-section">
                  <button className="retry-button" onClick={handleRetry} disabled={!onRetry}>
                    <span className="retry-icon">🔄</span>
                    Try Again
                  </button>
                </div>
              )}
            </div>
          );

        case 'text':
        default:
          // BACKWARD COMPATIBILITY: Handle legacy messages
          if (contentType === 'table' && tableData) {
            return (
              <div className="agent-ai-message-table-content">
                <div className="agent-ai-message-text">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[rehypeRaw, [rehypeSanitize, sanitizeSchema]]}
                    components={{ a: LinkRenderer, table: TableRenderer }}
                  >
                    {content}
                  </ReactMarkdown>
                </div>
                <div className="agent-ai-message-table">
                  <AgentDataTable
                    columns={tableData.columns}
                    rows={tableData.rows}
                    metadata={tableData.metadata}
                    onAction={handleTableAction}
                  />
                </div>
              </div>
            );
          }

          // Default text content
          return (
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw, [rehypeSanitize, sanitizeSchema]]}
              components={{ a: LinkRenderer, table: TableRenderer }}
            >
              {displayContent.text || content}
            </ReactMarkdown>
          );
      }
    };

    // STREAMING INDICATOR
    const isStreaming = messageObj && !messageObj.isComplete;

    // SHOW ACTIONS: Always show for response phase and error phase (includes legacy messages)
    const showActions =
      messageObj.phase === MessagePhase.RESPONSE ||
      messageObj.phase === MessagePhase.ERROR ||
      messageObj.phase === MessagePhase.STOPPED;

    // TD-2341: render every tool's execution as the shared one-liner (ripple · elapsed · tool
    // box · rotating word), retiring the old boxed phase status. Fired unconditionally so the
    // old box is fully replaced regardless of showClaudeDesktopStyle.
    if (messageObj.phase === MessagePhase.TOOL_EXECUTING) {
      // Elapsed timer counts from when the message entered tool execution (phaseStartTime).
      // Only read while the phase stays TOOL_EXECUTING, so it can never resurrect a stale clock
      // on a restored/finished turn; null for any message lacking a valid timestamp.
      const toolStartTime = messageObj.phaseStartTime
        ? new Date(messageObj.phaseStartTime).getTime()
        : null;
      return (
        <AgentMessageLoading
          mode="tool"
          toolName={messageObj.toolName}
          toolStartTime={toolStartTime}
        />
      );
    }

    // Claude Desktop UX: Show only loading indicator for thinking phase
    if (showClaudeDesktopStyle && messageObj.phase === MessagePhase.THINKING) {
      return <AgentMessageLoading />;
    }

    return (
      <div
        className={`agent-ai-message phase-${messageObj.phase || 'response'} ${isStreaming ? 'streaming' : 'complete'}`}
      >
        <div className="agent-ai-message-header">
          <span className="agent-ai-label">{agentName}</span>
          {isStreaming && (
            <div className="streaming-indicator">
              <div className="streaming-pulse"></div>
            </div>
          )}
        </div>

        <div ref={contentRef} className="agent-ai-message-content">
          {renderPhaseContent()}
        </div>

        {/* Only show actions for completed messages */}
        {showActions && (
          <div className="agent-ai-message-actions">
            <IconButton
              icon={ICON_BUTTON_ICONS.COPY}
              tooltipText={copyState.tooltip}
              tooltipPosition="bottom"
              withFill={false}
              withBorder={false}
              onClick={handleCopyToClipboard}
              disabled={copyState.status === 'copying'}
            />
          </div>
        )}
      </div>
    );
  }
);

export default AgentAIMessage;
